import React, { useRef, useEffect } from "react";
import Input from "Components/Common/Input/Input";
import "./FilterDropdown.scss";

const STATUS_OPTIONS = [
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inActive" },
  { label: "Pre-construction", value: "pre_construction" },
  { label: "Draw Request", value: "draw_request" },
  { label: "Completed", value: "completed" },
  { label: "Cancelled", value: "cancelled" },
];

const TYPE_OPTIONS = [
  { label: "Commercial", value: "commercial" },
  { label: "Residential", value: "residential" },
];

interface FilterOption {
  label: string;
  value: string;
}

interface Filters {
  status: string;
  type: string;
  lenderCompanyName: string;
  [key: string]: string;
}

interface FilterDropdownProps {
  open: boolean;
  filters: Filters;
  onChange: (filters: Filters) => void;
  onClose: () => void;
  bankOptions?: string[];
}

export default function FilterDropdown({
  open,
  filters,
  onChange,
  onClose,
  bankOptions = [],
}: FilterDropdownProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  const toggle = (group: string, value: string) => {
    onChange({ ...filters, [group]: filters[group] === value ? "" : value });
  };

  if (!open) return null;

  const groups: { key: string; label: string; options: FilterOption[] }[] = [
    { key: "status", label: "Status", options: STATUS_OPTIONS },
    { key: "type", label: "Project Type", options: TYPE_OPTIONS },
    {
      key: "lenderCompanyName",
      label: "Bank Name",
      options: bankOptions.map((b) => ({ label: b, value: b })),
    },
  ];

  return (
    <div className="fdd" ref={ref}>
      {groups.map((g) => (
        <div key={g.key} className="fdd-group">
          <div className="fdd-glabel">{g.label}</div>
          {g.options.length === 0 ? (
            <div className="fdd-empty">No options available</div>
          ) : (
            g.options.map((opt) => {
              const checked = filters[g.key] === opt.value;
              return (
                <label
                  key={opt.value}
                  className={`fdd-opt${checked ? " fdd-opt--sel" : ""}`}
                >
                  <Input
                    type="radio"
                    name={g.key}
                    checked={checked}
                    onChange={() => toggle(g.key, opt.value)}
                  />
                  {opt.label}
                </label>
              );
            })
          )}
        </div>
      ))}
    </div>
  );
}
